/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "ceco-polyglot";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import graphql from "babel-plugin-relay/macro";
import { MoreVertical, Edit, Delete } from "ceicons";
import Pill from "../Pill";
import { commitMutation, createRefetchContainer } from "react-relay";
import { StepRecipeDeleteModal } from "../../modals/StepRecipeDeleteModal";
import { StepRecipeModal } from "../../modals/StepRecipeModal";
import environment from "../../../environment";
import DropDownButton from "../../DropDownButton";

import "./stepList.scss";

function StepsList({
  recipe: { steps },
  recipeId,
  recipeName,
  relay,
  allWarnings,
  configConstance,
  summaryIngredient,
  refetchData,
}) {
  const t = useTranslation();

  const [stepList, setStepList] = useState(steps.edges);
  const isDragging = useRef(false);

  useEffect(() => {
    if (!isDragging.current) {
      setStepList(
        steps.edges.map((step, i) => {
          return step;
        })
      );
    }
  }, [steps]);

  const _refetch = (id) => {
    relay.refetch(id ? { id: id } : null, null, () => {}, { force: true });
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);

    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = reorder(
      stepList,
      result.source.index,
      result.destination.index
    );

    setStepList(items);

    return new Promise((resolve, reject) => {
      const variables = {
        recipe: recipeId,
        id: result.draggableId,
        ordered: result.destination.index + 1,
      };

      commitMutation(environment, {
        mutation: StepsListUpdateStepMutation,
        variables,
        onCompleted(response, errors) {
          if (errors) {
            reject(errors);
          }
          resolve(response);
          _refetch();
        },
        onError(error) {
          reject(error);
        },
      });
    });
  };

  return (
    <div className="pills steps-list">
      <DragDropContext
        onDragEnd={onDragEnd}
        onDragStart={() => (isDragging.current = true)}
      >
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {stepList &&
                stepList.map((step, i) => (
                  <Draggable
                    key={step.node.id}
                    draggableId={step.node.id}
                    index={i}
                  >
                    {(provided, snapshot) => (
                      <div
                        className={`pill`}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <span className="number">{i + 1}</span>
                        <span className="text">{step.node.description}</span>

                        <DropDownButton icon={<MoreVertical />}>
                          <StepRecipeModal
                            steps={stepList[i]}
                            recipeId={recipeId}
                            edit
                            isDragging={isDragging}
                            step={step.node}
                            allWarnings={allWarnings}
                            summaryIngredient={summaryIngredient}
                            refetchData={refetchData}
                            configConstance={configConstance}
                            trigger={
                              <div>
                                <Edit />
                                <p>{t("popup.edit")}</p>
                              </div>
                            }
                          />
                          <StepRecipeDeleteModal
                            recipeId={recipeId}
                            recipeName={recipeName}
                            edit
                            isDragging={isDragging}
                            step={step.node}
                            refetchData={refetchData}
                            trigger={
                              <div className="delete-modal">
                                <Delete />
                                <p>{t("popup.remove")}</p>
                              </div>
                            }
                          />
                        </DropDownButton>
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <StepRecipeModal
        recipeId={recipeId}
        allWarnings={allWarnings}
        isDragging={isDragging}
        summaryIngredient={summaryIngredient}
        configConstance={configConstance}
        refetchData={refetchData}
        title={
          <>
            <span className="number">{steps.edges.length + 1}</span>
            <span className="text">{t("pill.step")}</span>
          </>
        }
        trigger={
          <span>
            <Pill newItem text={t("pill.addStep")} />
          </span>
        }
      />
    </div>
  );
}

const StepsListUpdateStepMutation = graphql`
  mutation StepsListUpdateStepMutation($id: ID!, $ordered: Int!) {
    recipeStepUpdate(input: { id: $id, ordered: $ordered }) {
      recipeStep {
        id
        description
        ordered
      }
    }
  }
`;

export default createRefetchContainer(
  StepsList,
  {
    recipe: graphql`
      fragment StepsList_recipe on RecipeNode {
        steps(first: null) @connection(key: "StepsList_steps", filters: []) {
          edges {
            node {
              id
              ordered
              description
              time
              speed
              temperatureDegrees
              temperatureControl
              turbo
              isRobot
              waitingInstructions
              media
              warning {
                edges {
                  node {
                    id
                    slugName
                  }
                }
              }
              ingredient {
                id
                name
              }
              weight
              unit
            }
          }
        }
      }
    `,
  },
  graphql`
    query StepsListRefetchQuery($id: ID!) {
      recipe(id: $id) {
        ...StepsList_recipe
      }
    }
  `
);
